const getBaseDomain = (hostname: string): string => {
	const normalizedHost = hostname.toLowerCase();
	const segments = normalizedHost.split('.').filter(Boolean);

	if (segments.length <= 2) {
		return normalizedHost;
	}

	return segments.slice(-2).join('.');
};

// The domain configured for this deployment (e.g. "lovet.com"), used to recognize links that point back to this site.
const getTargetDomain = (): string => {
	const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

	if (!siteUrl) {
		return '';
	}

	try {
		return getBaseDomain(new URL(siteUrl).hostname);
	} catch {
		return getBaseDomain(siteUrl);
	}
};

// The real browser origin, if one exists yet. Never fall back to build-time config — it doesn't
// reflect which environment (localhost, staging, production) is actually serving the page.
const getCurrentOrigin = (): string => globalThis?.location?.origin ?? '';

// Pages not controlled by this codebase, so they must not be rewritten to the current origin.
const EXCLUDED_SUBDOMAINS = ['careers', 'forms'];

const isExcludedHost = (hostname: string, targetDomain: string): boolean =>
	EXCLUDED_SUBDOMAINS.some((subdomain) => hostname === `${subdomain}.${targetDomain}`);

const isTargetHost = (hostname: string, targetDomain: string): boolean =>
	!!targetDomain && (hostname === targetDomain || hostname.endsWith(`.${targetDomain}`));

const isHttpUrl = (protocol: string): boolean => protocol === 'http:' || protocol === 'https:';

/**
 * Rewrites absolute links pointing at the configured target domain to use the current origin
 * (localhost, staging, production - whichever is actually running), keeping the result absolute.
 * Leaves the href untouched when there's no browser origin yet (SSR with no window) - rewriting
 * to a root-relative path there would flip this link between two different render branches
 * (external anchor vs Next's internal Link) between the server and client passes, which is a
 * structural hydration mismatch, not just a harmless attribute diff.
 * Internal links, root-relative paths (starting with "/"), and excluded hosts (careers, forms)
 * are left untouched.
 */
export const normalizeDomainLink = (href?: string, isInternal?: boolean): string => {
	if (!href || isInternal || href.startsWith('/')) {
		return href ?? '';
	}

	let parsedUrl: URL;

	try {
		parsedUrl = new URL(href);
	} catch {
		return href;
	}

	if (!isHttpUrl(parsedUrl.protocol)) {
		return href;
	}

	const targetDomain = getTargetDomain();
	const hostname = parsedUrl.hostname.toLowerCase();

	if (!isTargetHost(hostname, targetDomain) || isExcludedHost(hostname, targetDomain)) {
		return href;
	}

	const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
	const currentOrigin = getCurrentOrigin();
	return currentOrigin ? `${currentOrigin}${path}` : href;
};

export const normalizeDomainDisplayLink = (text: string): string => {
	if (!text) {
		return '';
	}

	return normalizeDomainLink(text) ?? '';
};
